/**
 * The per-Rx COUNSELING block (GP-1358).
 *
 * Extracted verbatim from the RX History Rx card (`RxTimelineTab`) so the Rx
 * Detail dialog opened from Prescriptions & Drugs → All Prescriptions can render
 * the SAME card — status, method, counsellor, timestamp, decline reason,
 * checklist score, comments, and the View/Edit Counseling button — instead of
 * forcing a tab round-trip to Notes & Documents to find out whether the
 * prescription in hand was counselled. One component, so the two never drift
 * (see CLAUDE.md: one source of truth).
 */
import React, { useMemo } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { RecordVoiceOver as CounselIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useBatchUserLookup } from '../../hooks/useUserLookup';
import {
  getCounselingStatusDisplay,
  getCounselorDisplayName,
  getDeclinedReasonLabel,
} from '../../constants/counselingConstants';

/**
 * The subset of a counseling record this card renders. Deliberately loose — both
 * checklist schemas (the legacy 13-item shape and the per-ISEC-point v2 shape)
 * flow through here, so the count is derived from the record itself.
 */
export interface RxCounselingRecordLike {
  id: string;
  status: string;
  method?: string;
  declinedReason?: string;
  comments?: string;
  checklist?: unknown;
  createdBy?: string;
  completedBy?: string;
  createdAt: string;
  completedAt?: string;
}

export interface RxCounselingCardProps<T extends RxCounselingRecordLike> {
  records: T[];
  /** Resolve an auth0 id to a display name. Falls back to a batch lookup. */
  resolveUser?: (value: string | undefined | null) => string;
  /** Open the counseling record (renders the View/Edit Counseling button). */
  onOpenRecord?: (record: T) => void;
  /** Shown when the Rx has no counseling record at all. */
  emptyText?: string;
  /** Render the "COUNSELING" heading above the records. */
  showHeader?: boolean;
}

/** "12/21" — checked points over the points the record actually carries. */
export function getChecklistProgress(checklist: unknown): string {
  if (!checklist || typeof checklist !== 'object') return '0/13';
  const points = Object.values(checklist as Record<string, { checked?: boolean } | undefined>);
  const checked = points.filter((v) => v?.checked).length;
  return `${checked}/${points.length}`;
}

/**
 * GP-1358 — flatten counseling records into the display-only rows the hardcopy
 * prints (`HardcopyViewer.counselingRecords`). Keeping the formatting here means
 * the printed counseling block and the on-screen card say the same thing, and
 * the hardcopy stays a presentation component (no counseling domain logic, no
 * user lookups).
 */
export function toHardcopyCounselingEntries(
  records: RxCounselingRecordLike[] | null | undefined,
  resolveUser?: (value: string | undefined | null) => string,
): Array<{
  id: string;
  statusLabel: string;
  method?: string;
  counselorName?: string;
  counselledAt?: string;
  declinedReason?: string;
  checklistProgress?: string;
  comments?: string;
}> {
  return (records || []).map((rec) => {
    const by = rec.completedBy || rec.createdBy;
    return {
      id: rec.id,
      statusLabel: getCounselingStatusDisplay(rec.status, rec.declinedReason).label,
      method: rec.method ? rec.method.replace(/_/g, ' ') : undefined,
      // GP-1485: an unresolved id must print as "Unknown user", never as the
      // id itself — an auth0 sub on a printed sheet is both unreadable and a
      // leak, and `resolveUser` (the caller's batch map) can miss.
      counselorName: by ? getCounselorDisplayName(by, resolveUser) : undefined,
      counselledAt: formatCounselledAt(rec.completedAt || rec.createdAt),
      declinedReason: rec.declinedReason ? getDeclinedReasonLabel(rec.declinedReason) : undefined,
      checklistProgress: rec.checklist ? getChecklistProgress(rec.checklist) : undefined,
      comments: rec.comments,
    };
  });
}

/**
 * When the session happened, as it prints on the hardcopy.
 *
 * Formatted HERE rather than in `HardcopyViewer` because the viewer's own
 * `formatDate` is built for Kroll's timezone-less date columns: it strips a
 * trailing `Z` and re-reads the numbers as local time. A counseling timestamp is
 * a Postgres `timestamptz` (`2026-08-07T21:03:00.000Z`), so that treatment
 * printed 9:03 PM where the counseling card on the same screen said 5:03 PM
 * (GP-1358). Parsing the instant and formatting it locally is what the card
 * does, so the printed sheet and the screen now agree.
 *
 * An absent or unparseable value yields no label rather than reaching `format()`
 * — date-fns throws a RangeError on an Invalid Date, which would take the whole
 * hardcopy render down over a missing timestamp.
 */
function formatCounselledAt(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  // Same day/month/year style the rest of the hardcopy prints (dd-MMM-yyyy).
  return format(parsed, 'dd-MMM-yyyy h:mm a');
}

export function RxCounselingCard<T extends RxCounselingRecordLike>({
  records,
  resolveUser,
  onOpenRecord,
  emptyText = 'No counseling record',
  showHeader = true,
}: RxCounselingCardProps<T>) {
  // Only used when the caller doesn't already resolve names (the RX History tab
  // batches the lookup across every Rx on screen and passes `resolveUser`).
  const ids = useMemo(
    () => (resolveUser ? [] : records.flatMap((r) => [r.completedBy, r.createdBy])),
    [records, resolveUser],
  );
  const { userNames } = useBatchUserLookup(ids);
  const displayUser = (value: string | undefined | null): string => {
    if (!value) return '';
    // GP-1485: whichever resolver answers, the raw value is never the fallback —
    // an id that can't be resolved (batch still in flight, or a user the lookup
    // doesn't know) reads as "Unknown user", not as an auth0 sub.
    return getCounselorDisplayName(value, resolveUser ?? ((id) => userNames.get(id)));
  };

  return (
    <Box data-testid="rx-counseling-card">
      {showHeader && (
        <Typography
          variant="caption"
          fontWeight={700}
          color="primary.main"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}
        >
          <CounselIcon sx={{ fontSize: 14 }} /> COUNSELING
        </Typography>
      )}
      {records.length > 0 ? (
        records.map((rec) => (
          <Box key={rec.id} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
              {(() => {
                const s = getCounselingStatusDisplay(rec.status, rec.declinedReason);
                return <Chip label={s.label} size="small" color={s.color} sx={{ height: 20, fontSize: '0.7rem' }} />;
              })()}
              {rec.method && (
                <Typography variant="caption" color="text.secondary">{rec.method.replace(/_/g, ' ')}</Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {displayUser(rec.completedBy || rec.createdBy)}
              </Typography>
              {(rec.completedAt || rec.createdAt) && (
                <Typography variant="caption" color="text.secondary">
                  {format(new Date(rec.completedAt || rec.createdAt), 'MMM d, yyyy h:mm a')}
                </Typography>
              )}
            </Box>
            {rec.declinedReason && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                Reason: {getDeclinedReasonLabel(rec.declinedReason)}
              </Typography>
            )}
            {rec.checklist ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Checklist: {getChecklistProgress(rec.checklist)}
              </Typography>
            ) : null}
            {rec.comments && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                "{rec.comments}"
              </Typography>
            )}
            {onOpenRecord && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<CounselIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => { e.stopPropagation(); onOpenRecord(rec); }}
                sx={{ mt: 0.5 }}
              >
                {rec.status === 'completed' ? 'View Counseling' : 'Edit Counseling'}
              </Button>
            )}
          </Box>
        ))
      ) : (
        <Typography variant="caption" color="text.secondary">{emptyText}</Typography>
      )}
    </Box>
  );
}

export default RxCounselingCard;
